/**
 * 提醒内核 —— **状态派生 / 三档分组 / 档位映射 / 免打扰 / 推送决策 / 幂等窗口键 / 合并**
 * （**H 域唯一实现** · 架构 §3.4「`reminder` 模块 = 五类规则 · 幂等键 · 三档偏好 · 推送决策」）
 *
 * 依据：H 域 PRD **§5.1.2**（提前量与重复策略）· **§5.1.3**（去重与合并）·
 * **§5.1.4**（优先级与分组 · 对齐原型）· **§5.3**（三档 / 五类开关 / 免打扰）·
 * **§6.1**（异常处置）· **§7.1**（`AC-H1.8` / `AC-H1.9` / `AC-H1.10` / `AC-H1.15`）·
 * `DR-038`（状态载体）· 架构 **§3.4 / §4.2 E 段** · `DR-011` §6（**G 算 H 拉**）。
 *
 * ## 🔴 本文件**不碰数据库、不碰 HTTP** —— 纯函数，可离线单测
 *
 * | 谁 | 做什么 |
 * |---|---|
 * | **本内核** | 「是什么状态 / 归哪一组 / 该不该推 / 窗口键是什么 / 合并后合计多少」 |
 * | `modules/reminder` | 取数（信用卡 / 预算快照 / 偏好）· 落库 · 下发 |
 * | **端侧** | 🔴 **零形态规则** —— 只转发中文文案（动作/标题文案的唯一源在 `features/reminder-labels.ts`） |
 *
 * ## 🔴 三条最容易被写错的判定（本节反复强调）
 *
 * 1. **窗口键必须含日期** —— 见 `reminderWindowKey()` 的文件注释：只写「提前 5 天」会让
 *    **下一期账单撞上一期的键**（`uq_reminder_window` 把它挡掉 ⇒ 第二个月漏推）。
 * 2. **免打扰"不推"≠"不记"** —— 免打扰只否决**推送**（`shouldPushReminder` 返回
 *    `do_not_disturb`），**站内中心照常记录**（`AC-H3.5`：免打扰期间的提醒**静默累积不丢弃**）。
 * 3. **`read` 与 `done` 是两个独立事实** —— 读了但没做完，**仍应当再推**（`AC-H1.14` 逾期每日提醒
 *    的用户对象正是"看过就忘了"的人）⇒ `settled` 只认 `done` / `dismissed`，**不认 `read`**。
 */

import type { Cents } from '../types/brand.js';
import {
  ReminderGroup,
  ReminderLevel,
  ReminderState,
  ReminderType,
  ReminderVariant,
} from '../enums/plan.js';
import { ReminderPolicy } from '../constants/index.js';

// ═══════════════════════════ ① 状态派生（`DR-038`） ═══════════════════════════

/** 状态派生的输入 = `reminder_instances` 的三个状态列（其余列与本判定无关） */
export interface ReminderStateInput {
  readAt: number | null;
  doneAt: number | null;
  dismissedAt: number | null;
}

/**
 * 🔴 **状态派生唯一实现**（`DR-038` §2 · `AC-H3.7`）。
 *
 * **顺序判定**（顺序即优先级，不可调换）：
 * `dismissed` > `done` > `read` > `unread`。
 *
 * 🔴 **为什么不存枚举列**：三事件**可叠加** —— 用户「读了」之后才「做完」，
 * 单枚举列会把 `readAt` 冲掉，而 P1 的推送决策正是靠「`readAt` 非空且 `doneAt` 为空」
 * 区分「看过就忘了的人」（`DR-038` §3）。
 */
export function reminderStateOf(row: ReminderStateInput): ReminderState {
  if (row.dismissedAt !== null) return ReminderState.Dismissed;
  if (row.doneAt !== null) return ReminderState.Done;
  if (row.readAt !== null) return ReminderState.Read;
  return ReminderState.Unread;
}

// ═══════════════════════════ ② 三档分组（`AC-H1.10`） ═══════════════════════════

/**
 * 🔴 **分组唯一实现**（原型 `09-提醒中心_原型.html`「需要你处理 / 知道就好 / 更早」）。
 *
 * | 组 | 判据（`§5.1.4`） | 本实现 |
 * |---|---|---|
 * | `action` | 有**明确可执行动作**且有时效 | **还款日**（要还款）· **记账习惯**（要补记） |
 * | `info` | 无需动作，或节奏正常 | **账单出账通知**（只是告知）· **预算进度** · **目标进度** |
 * | `earlier` | 已读 / 已完成的历史 | 🔴 **状态 ≠ `unread` 一律沉底**（原型「更早」档） |
 *
 * ⚠️ **预算超支（`over`）归 `info` 而非 `action`** —— 依据 `§5.1.4` 的原文示例：
 * 「需要你处理」列的是**还款、房贷到期、查漏补记**，**不含预算**；原型里
 * 「餐饮预算用掉 62%」也确实在「知道就好」组。🔴 **不按"看起来严重"自行升级**。
 */
export function reminderGroupOf(input: {
  type: ReminderType;
  state: ReminderState;
}): ReminderGroup {
  // 「更早」= 已读 / 已完成 / 已忽略 —— 已读即沉底（原型行为）
  if (input.state !== ReminderState.Unread) return ReminderGroup.Earlier;
  if (input.type === ReminderType.Repayment || input.type === ReminderType.Habit) {
    return ReminderGroup.Action;
  }
  return ReminderGroup.Info;
}

/**
 * 🔴 该类型**是否有可执行动作**（`AC-H1.11`：每条要么给动作、要么明确「无需处理」）。
 *
 * ⚠️ **只看类型，不看 `state`** —— 与 {@link reminderGroupOf} 的**唯一区别**就在这里：
 * 分组要「已读即沉底」，而"有没有动作"**不因为你看过一眼就消失**（已读 ≠ 已处理）。
 * 所以两者**不能互相派生**：`group === Action` 会随 `state` 变化，而本函数不会。
 *
 * ## 🔴 为什么它是**类型级**而非**实体级**（`63` 批真 bug）
 *
 * 首版把它放在"实体快照"（服务端 `SubjectView.actionable`）里，于是：
 * **同一张信用卡**在「还款日临近」时是"要还款"、在「账单日出账」时只是"告知"——
 * 而**实体只有一份** ⇒ 后者被前者的 `true` 污染 ⇒ **账单提醒谎称"需要你处理"**
 * （与本文档上方的分组表直接矛盾：账单出账通知属于"只是告知"）。
 *
 * ⚠️ 这个 bug **长期被一个空转断言掩盖**：集成测试里 `actualBilling` 恰好在那些天为空
 * ⇒ `for (const it of actualBilling)` **一次都没进循环** ⇒ 假通过。见 `63` 批 changelog §3.5 坑④。
 */
export function reminderIsActionable(type: ReminderType): boolean {
  return type === ReminderType.Repayment || type === ReminderType.Habit;
}

// ═══════════════════════════ ③ 档位映射（`AC-H3.1`） ═══════════════════════════

/** 档位强弱（**数字越大越"吵"** —— 比较用它，不用字符串） */
const LEVEL_RANK: Readonly<Record<ReminderLevel, number>> = {
  [ReminderLevel.KeyOnly]: 0,
  [ReminderLevel.Normal]: 1,
  [ReminderLevel.All]: 2,
};

/**
 * 🔴 **某类型/变体的最低档位**（H 域 PRD **§5.3.1** 表）。
 *
 * | 档 | 覆盖 | 本实现 |
 * |---|---|---|
 * | **只推关键** | 还款日 · 账单到期 · **超支** | `repayment` / `billing` / `budget:over` |
 * | **常规**（默认） | 关键 + 预算预警 + 目标进度 + 每周检视 | `budget:warn` / `goal` / `habit:weekly` |
 * | **全部** | 以上 + 每日记账提醒 + 新账单导入提示 | `habit:catchup` 等其余习惯子类 |
 */
export function reminderMinLevel(type: ReminderType, variant: ReminderVariant): ReminderLevel {
  switch (type) {
    case ReminderType.Repayment:
    case ReminderType.Billing:
      return ReminderLevel.KeyOnly;
    case ReminderType.Budget:
      return variant === ReminderVariant.BudgetOver ? ReminderLevel.KeyOnly : ReminderLevel.Normal;
    case ReminderType.Goal:
      return ReminderLevel.Normal;
    case ReminderType.Habit:
      return variant === ReminderVariant.WeeklyReview ? ReminderLevel.Normal : ReminderLevel.All;
    default:
      // 运行时兜底（值来自 DB，可能是未来新增的 type）—— 取最保守的「常规」，**不静默当成关键**
      return ReminderLevel.Normal;
  }
}

/** 是否属「关键类」（端侧据此上色 —— 判定归内核，**端侧零规则**） */
export function reminderIsKey(type: ReminderType, variant: ReminderVariant): boolean {
  return reminderMinLevel(type, variant) === ReminderLevel.KeyOnly;
}

// ═══════════════════════════ ④ 免打扰（`AC-H3.4` / `AC-H3.5`） ═══════════════════════════

/** `HH:mm` → 当日分钟数（`00:00` = 0，`23:59` = 1439）；非法即抛（不静默取 0） */
export function minutesOfClock(hhmm: string): number {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (m === null) throw new RangeError(`本地时刻格式须为 HH:mm：${hhmm}`);
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) throw new RangeError(`本地时刻越界（HH:mm）：${hhmm}`);
  return h * 60 + min;
}

/**
 * 🔴 **免打扰判定**（**半开区间** `[start, end)` · 支持跨零点环绕）。
 *
 * 默认 `22:00 – 07:30` **跨零点** ⇒ `s < e` 的分支**必须**与环绕分支分开写，
 * 否则 `22:30` 会被判成"不在区间"（最典型的一种静默失效）。
 *
 * 🔴 `start === end` ⇒ **恒 `false`**（零长度窗口 = 不生效）——
 * **不把它解释成"全天免打扰"**（那会把用户的通知全静默掉，且是"配置看起来正常"的故障）。
 */
export function isWithinDoNotDisturb(localTime: string, start: string, end: string): boolean {
  const t = minutesOfClock(localTime);
  const s = minutesOfClock(start);
  const e = minutesOfClock(end);
  if (s === e) return false;
  return s < e ? t >= s && t < e : t >= s || t < e;
}

/**
 * 🔴 **写入侧校验**（`N` 域设置页保存免打扰时调它）—— 把「配了但静默不生效」挡在保存之前。
 *
 * 拦两件事（两件都会让免打扰**看起来配好了、实际从不生效**）：
 * 1. **起点 = 终点**：`isWithinDoNotDisturb()` 明确把零长度窗口判为"不生效"（见其注释）——
 *    那是**运行期的兜底**，不该让用户**存进来**；
 * 2. **格式非法**：`HH:mm` 之外的写法（`7:30` / `24:00` / 空串）—— `minutesOfClock()` 会抛，
 *    但在这里抛能给出**面向用户的错误码**，而不是 500。
 *
 * @throws RangeError 时刻格式非法，或 起点 === 终点
 */
export function assertDoNotDisturbRange(start: string, end: string): void {
  // 先让 `minutesOfClock` 校验格式（它抛 `RangeError`，带具体值）
  const s = minutesOfClock(start);
  const e = minutesOfClock(end);
  if (s === e) {
    throw new RangeError('免打扰的起止时刻不能相同（那等于免打扰从不生效）');
  }
}

// ═══════════════════════════ ⑤ 推送决策（`AC-H3.1` / `AC-H3.3` / `AC-H3.4`） ═══════════════════════════

/** 推送被否决的原因（**可测试的枚举** —— 不用字符串文案当判据） */
export type PushSkipReason = 'settled' | 'type_off' | 'below_level' | 'do_not_disturb';

export interface PushDecision {
  push: boolean;
  reason: 'ok' | PushSkipReason;
}

/** 推送决策所需的偏好（**由 `user_preferences` 行映射** —— 见 `reminderPrefsOf()`） */
export interface ReminderPrefsInput {
  level: ReminderLevel;
  /** 五类按类型开关（H 域 PRD §5.3.2） */
  typeEnabled: Readonly<Record<ReminderType, boolean>>;
  dndEnabled: boolean;
  /** `HH:mm`（**账本时区**下的本地时刻） */
  dndStart: string;
  dndEnd: string;
}

/**
 * 🔴 **推送决策唯一实现**（H 域 PRD **§5.3** / `AC-H3.1` / `AC-H3.3` / `AC-H3.4`）。
 *
 * 判定顺序（**固定**，每一步都能单独被 AC 判到）：
 *
 * 1. `settled`（`done` / `dismissed`）⇒ **永不再推** —— 🔴 **`read` 不在此列**
 *    （「看过就忘了」的人正是逾期每日提醒的对象，`AC-H1.14`）；
 * 2. `type_off`（五类开关，`§5.3.2`）；
 * 3. `below_level`（三档，`§5.3.1`）；
 * 4. `do_not_disturb`（`§5.3.3` —— 🔴 **无例外，连关键提醒也不推**）。
 *
 * 🔴 **本函数只回答"推不推"**，**不回答"记不记"** —— 站内提醒中心**恒记录**
 * （`§5.2.2` 可用性铁律 + `AC-H3.5` 静默累积不丢弃）。调用方**不得**因 `push:false` 而不落库。
 */
export function shouldPushReminder(input: {
  type: ReminderType;
  variant: ReminderVariant;
  state: ReminderState;
  prefs: ReminderPrefsInput;
  /** 账本时区下的本地时刻 `HH:mm`（调用方用 `localClockOf()` 求） */
  localTime: string;
}): PushDecision {
  const { type, variant, state, prefs, localTime } = input;

  if (state === ReminderState.Done || state === ReminderState.Dismissed) {
    return { push: false, reason: 'settled' };
  }
  if (!prefs.typeEnabled[type]) return { push: false, reason: 'type_off' };
  if (LEVEL_RANK[prefs.level] < LEVEL_RANK[reminderMinLevel(type, variant)]) {
    return { push: false, reason: 'below_level' };
  }
  if (prefs.dndEnabled && isWithinDoNotDisturb(localTime, prefs.dndStart, prefs.dndEnd)) {
    return { push: false, reason: 'do_not_disturb' };
  }
  return { push: true, reason: 'ok' };
}

// ═══════════════════════════ ⑥ 偏好映射与兜底 ═══════════════════════════

/** `user_preferences` 行里**与本域相关**的列（H 域**只读消费** · `DR-020` §5） */
export interface ReminderPrefsRowInput {
  reminderLevel: ReminderLevel;
  remindRepayment: boolean;
  remindBilling: boolean;
  remindBudget: boolean;
  remindGoal: boolean;
  remindHabit: boolean;
  doNotDisturbEnabled: boolean;
  doNotDisturbStart: string;
  doNotDisturbEnd: string;
}

/** 把 `user_preferences` 行映射成内核输入（🔴 **映射唯一实现** —— 防止两处各写一遍五类开关） */
export function reminderPrefsOf(row: ReminderPrefsRowInput): ReminderPrefsInput {
  return {
    level: row.reminderLevel,
    typeEnabled: {
      [ReminderType.Repayment]: row.remindRepayment,
      [ReminderType.Billing]: row.remindBilling,
      [ReminderType.Budget]: row.remindBudget,
      [ReminderType.Goal]: row.remindGoal,
      [ReminderType.Habit]: row.remindHabit,
    },
    dndEnabled: row.doNotDisturbEnabled,
    dndStart: row.doNotDisturbStart,
    dndEnd: row.doNotDisturbEnd,
  };
}

/**
 * 🔴 **无偏好行时的兜底**（N 域 `user_preferences` 尚未写入的成员 / 新账本）。
 *
 * 取**表默认值**（`a-identity.ts` `userPreferences` 的 `.default()`）：三档 `normal`、
 * 五类全开、免打扰 `22:00–07:30` 开 —— 即 `AC-H3.2`「默认档位 = 常规」。
 * 🔴 **与"查到了但都是默认值"结果相同**，但**语义不同**（一个"还没设过"，一个"设成了默认"）；
 * 本批两者行为一致，**故不额外区分**（若将来"是否设过"要驱动 UI 引导，再加显式旗标）。
 */
export function defaultReminderPrefs(): ReminderPrefsInput {
  return {
    level: ReminderLevel.Normal,
    typeEnabled: {
      [ReminderType.Repayment]: true,
      [ReminderType.Billing]: true,
      [ReminderType.Budget]: true,
      [ReminderType.Goal]: true,
      [ReminderType.Habit]: true,
    },
    dndEnabled: true,
    dndStart: ReminderPolicy.DefaultDndStart,
    dndEnd: ReminderPolicy.DefaultDndEnd,
  };
}

// ═══════════════════════════ ⑦ 幂等窗口键（`AC-H1.8` / `AC-H1.15`） ═══════════════════════════

/**
 * 🔴 **窗口键唯一实现**（幂等唯一键 = `(ledgerId, type, entityId, windowKey)` · `AC-H1.8`）。
 *
 * ⚠️ **必须把日期编进窗口**（架构 §3.4 的示例就是 `2026-09` / `2026-09-30`）：
 * 若只写 `d-5`，那么**同一个账户下一期账单的提醒会撞上一期的键** ——
 * `uq_reminder_window` 直接把它挡掉 ⇒ **第二个月起静默漏推**（`AC-H1.12` 零遗漏的反面）。
 *
 * @param variant 提醒变体（决定窗口前缀）
 * @param anchor  窗口锚点 = 该窗口对应的**本地日历日**（到期日 / 账单日 / 逾期当日 / 预算窗口键）
 */
export function reminderWindowKey(input: { variant: ReminderVariant; anchor: string }): string {
  const { variant, anchor } = input;
  if (anchor === '') throw new RangeError('窗口锚点不得为空（幂等键必须含日期 —— 否则跨期撞车）');
  switch (variant) {
    case ReminderVariant.DueSoon:
      return `d-${ReminderPolicy.RepaymentLeadDays}:${anchor}`;
    case ReminderVariant.DueToday:
      return `d-0:${anchor}`;
    case ReminderVariant.Overdue:
      return `od:${anchor}`;
    case ReminderVariant.BillIssued:
      return `b+${ReminderPolicy.BillingNotifyAfterDays}:${anchor}`;
    case ReminderVariant.BudgetWarn:
      return `${anchor}:warn`;
    case ReminderVariant.BudgetOver:
      return `${anchor}:over`;
    case ReminderVariant.GoalProgress:
      return `goal:${anchor}`;
    case ReminderVariant.WeeklyReview:
      return `weekly:${anchor}`;
    case ReminderVariant.CatchUp:
      return `catchup:${anchor}`;
    default:
      throw new RangeError(`未知提醒变体（窗口键无法生成）：${String(variant)}`);
  }
}

// ═══════════════════════════ ⑧ 合并（`AC-H1.9`） ═══════════════════════════

/**
 * 🔴 **合并分组键**（`AC-H1.9`：同一还款窗口的多张卡 ⇒ **合并为 1 条**）。
 *
 * 合并范围 = **同类型 + 同窗口键** ⇒ 同一天到期的两张卡走同一个 `windowKey`（窗口键含到期日），
 * **不同到期日不合**（跨日合并会把"5 天后"和"8 天后"揉成一条，用户没法据此决定先还哪张）。
 */
export function reminderMergeKey(input: { type: ReminderType; windowKey: string }): string {
  return `${input.type}|${input.windowKey}`;
}

/**
 * 合并后的合计金额。
 *
 * 🔴 **全为 `null` ⇒ 返回 `null`** —— **不把"这条没有金额"当成 `0`**
 * （同 `AC-K4.x` 的「未就绪 ≠ 数据不足」纪律：沉默的 `0` 是假信息）。
 * 只要有一条非 `null`，就照实累加（`null` 项按"未计入"处理，**并应由调用方标注**）。
 */
export function sumReminderAmounts(amounts: readonly (number | null)[]): Cents | null {
  let total = 0;
  let has = false;
  for (const a of amounts) {
    if (a === null) continue;
    total += a;
    has = true;
  }
  return has ? (total as Cents) : null;
}
